/** @jsxImportSource @emotion/react */
import {
  Button,
  createStyles,
  Icon,
  List,
  ListItem,
  Loader,
  Modal,
  Snackbar,
  Tooltip,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { useGetTransactionDetails } from 'api/walletApi';
import { type AuthorizedBy } from 'api/walletApi.types';
import { useGetRecipient } from 'api/walletRecipientApi';
import { useOrganisation } from 'contexts';
import { TransactionType } from 'types/transactions';
import { useGenerateReceiptPDF } from '../hooks';
import { mapTransactionTypeToText } from './utils';

interface Props {
  merchantId: string;
  transactionId: string;
  authorizedBy?: AuthorizedBy;
}

export const TransactionDetails = ({
  transactionId,
  merchantId,
  authorizedBy,
}: Props) => {
  const styles = useStyles();
  const { spacing, palette, sizing } = useTheme();
  const [popupBlocked, setPopupBlocked] = useState(false);

  const {
    data: transaction,
    isLoading: loading,
    refetch: refetchTransactions,
    error,
  } = useGetTransactionDetails(merchantId, transactionId);

  const contactId = transaction?.destination?.contactId;
  const { data: recipient } = useGetRecipient({
    merchantId,
    contactId: contactId ?? '',
    enabled: !!contactId,
  });

  const {
    generateReceiptPDF,
    error: pdfError,
    setError: setPDFError,
  } = useGenerateReceiptPDF();

  const {
    selectedMerchant: { name: merchantName },
  } = useOrganisation();

  const openTravelRuleForm = () => {
    if (!transaction?.travelRuleUrl) {
      return;
    }
    const openedWindow = window.open(transaction.travelRuleUrl, '_blank');
    if (!openedWindow) {
      setPopupBlocked(true);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error || !transaction) {
    return (
      <div css={{ display: 'grid', gap: spacing.xs, textAlign: 'center' }}>
        <Typography variant="bodyBold" color={palette.danger.default}>
          Error in fetching the transaction details
        </Typography>
        <Button onClick={() => void refetchTransactions()} text="Try Again" />
      </div>
    );
  }

  const needsTravelRuleAction =
    transaction.status === 'PENDING_TRAVEL_RULE' &&
    transaction.type === TransactionType.RECEIVE_EXTERNAL;

  return (
    <div
      css={{ minWidth: sizing.large, maxHeight: '90svh', overflowX: 'scroll' }}
    >
      {needsTravelRuleAction && (
        <div css={styles.actionRequiredBanner}>
          <Icon name="error" size={spacing.xxs} />
          <Typography variant="bodySmall" color={palette.danger.default}>
            Action required: Provide originator information to complete this
            transaction
          </Typography>
          <Button text="Provide Info" onClick={openTravelRuleForm} />
        </div>
      )}
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: spacing.xxxs,
          marginBottom: spacing.xxxs,
        }}
      >
        {transaction.type === TransactionType.SEND_EXTERNAL &&
          transaction.destination?.blockchainTransactionId && (
            <Tooltip title="View in blockchain explorer" placement="top">
              <div
                css={[
                  styles.downloadButton,
                  {
                    svg: {
                      stroke: palette.black,
                    },
                    cursor: 'pointer',
                  },
                ]}
                onClick={() =>
                  window.open(
                    `https://etherscan.io/tx/${transaction.destination?.blockchainTransactionId}`,
                    '_blank'
                  )
                }
              >
                <Icon name="visibility-on" />
              </div>
            </Tooltip>
          )}
        {transaction.type == TransactionType.SEND_EXTERNAL && (
          <Tooltip title="Download as PDF" placement="top">
            <div
              css={styles.downloadButton}
              onClick={() => {
                generateReceiptPDF({
                  transaction: transaction,
                  recipient: recipient,
                  authorizedBy,
                });
              }}
            >
              <Icon
                name="download"
                size={`calc(${spacing.xxs} + calc(${spacing.xxxs}/4))`}
              />
            </div>
          </Tooltip>
        )}
      </div>
      <List>
        <ListItem title={'Transaction ID'} subcontent={transactionId} />
        <ListItem
          title={'Type'}
          subcontent={mapTransactionTypeToText(transaction.type)}
        />
        <ListItem
          title={'Created At'}
          subcontent={`${transaction.createdAt.toLocaleDateString()}, ${transaction.createdAt.toLocaleTimeString()}`}
        />
        {authorizedBy && (
          <ListItem title="Initiated by" subcontent={authorizedBy.email} />
        )}
        <ListItem
          title={'Amount'}
          subcontent={`${transaction.amount} ${transaction.currency}`}
        />
        <ListItem
          title={'Fee'}
          subcontent={`${Number(transaction.fee).toFixed(2)} EUR`}
        />
        {transaction.type === TransactionType.SEND_EXTERNAL && (
          <>
            <ListItem
              title={'Network Fee'}
              subcontent={`${transaction.networkFee} ${transaction.currency}`}
            />
            {merchantName && (
              <ListItem title="Sender name" subcontent={merchantName} />
            )}
            {transaction.destination && (
              <>
                {recipient?.label && (
                  <ListItem
                    title="Recipient name"
                    subcontent={recipient.label}
                  />
                )}
                <ListItem
                  title="Recipient address"
                  subcontent={transaction.destination.address}
                />
              </>
            )}
          </>
        )}
      </List>
      {popupBlocked && (
        <Modal closeModal={() => setPopupBlocked(false)}>
          <div
            css={{
              display: 'grid',
              gap: spacing.xs,
              maxWidth: sizing.small,
              textAlign: 'center',
            }}
          >
            <Typography variant="bodySmall" color={palette.danger.default}>
              Travel rule form window blocked from opening. Please allow popups
              and try again.
            </Typography>
            <Button
              onClick={() => {
                setPopupBlocked(false);
                openTravelRuleForm();
              }}
              text="Open travel rule form"
            />
          </div>
        </Modal>
      )}
      <Snackbar
        variant="danger"
        message="Error in generating PDF, please try again later"
        open={pdfError}
        onClose={() => {
          setPDFError(false);
        }}
      />
    </div>
  );
};

const useStyles = createStyles(({ spacing, palette }) => ({
  downloadButton: {
    height: spacing.s,
    width: spacing.s,
    borderRadius: '50%',
    background: palette.grey.x100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  actionRequiredButton: {
    background: `${palette.danger.default}20`,
    svg: {
      stroke: palette.danger.default,
    },
    '&:hover': {
      background: `${palette.danger.default}40`,
    },
  },
  actionRequiredBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xxs,
    padding: spacing.xxs,
    marginBottom: spacing.xs,
    backgroundColor: `${palette.danger.default}10`,
    borderRadius: spacing.xxxs,
    border: `1px solid ${palette.danger.default}`,
    svg: {
      fill: palette.danger.default,
      flexShrink: 0,
    },
  },
}));
